'use client';

import { fetchGoodsList } from '@/api/product';
import { IProductListApiParams } from '@/api/types/product';
import InfiniteScroll from '@/components/InfiniteScroll';
import Pagination from '@/components/Pagination';
import ProductCard from '@/components/Product/ProductCard';
import SortMenu from '@/components/SortMenu';
import { useStore } from '@/providers/app-store-provider';
import { DEFAULT_SORT_VALUE } from '@/utils/constants';
import { EPlatform } from '@/utils/enums/platform-enum';
import Image from '@/components/Image';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface ActivityProductsData {
  list: ActivityProduct[];
  total: number;
}

// 通用的商品类型定义
export interface ActivityProduct {
  id?: number | string;
  name?: string;
  short_name?: string;
  shop_sku?: string;
  business_sku?: string;
  image?: {
    product?: string[];
  };
  price_info?: Array<{
    currency?: string;
    price?: string;
    sell_price?: string;
  }>;
  [key: string]: any;
}

export interface ActivityProductGridProps {
  // 基本配置
  id?: string;
  title?: string | ReactNode;
  className?: string;

  // 数据配置 - 二选一
  staticProducts?: ActivityProduct[]; // 静态商品数据
  staticTotal?: number; // 静态商品数据总条数（来自RSC数据）
  apiConfig?: {
    // API配置
    group_id: string | number;
    limit?: number;
    in_stock?: number; // 库存过滤：1=有库存, 3=不过滤
    is_sale?: number; // 上架过滤：1=已上架, 3=不过滤
  };

  // 功能开关
  showSort?: boolean; // 是否显示排序
  showInfiniteScroll?: boolean; // 是否显示无限滚动
  showPagination?: boolean; // 是否显示分页器（与 showInfiniteScroll 互斥）

  // 布局配置
  deviceType?: EPlatform;
  wapColumns?: number; // WAP端列数，默认2
  pcColumns?: {
    lg: number;
    xl?: number;
    '2xl'?: number;
  }; // PC端列数配置

  // 间距配置
  padding?: {
    wap?: string; // WAP端左右间距，默认 'px-[8px]'
    pc?: string; // PC端左右间距，默认 'px-[120px]'
  };

  // 吸顶配置
  stickyTop?: {
    wap?: string; // WAP端吸顶距离，默认 'top-[11.733vw]'
    pc?: string; // PC端吸顶距离，默认 'top-[100px]'
  };

  // ProductCard配置
  productCardProps?: {
    showTryOn?: boolean;
    showCorrelation?: boolean;
    showReviewStar?: boolean;
    imageClassName?: string;
    className?: string;
  };

  // 事件回调
  onProductClick?: (product: ActivityProduct, index: number) => void;
  onProductExp?: (product: ActivityProduct, index: number) => void;
  onSortChange?: (sortValue: string | number) => void;
  onPageChange?: (page: number) => void; // 分页器页面变化回调
  onLoadComplete?: (hasMore: boolean) => void; // 加载完成回调，hasMore表示是否还有更多数据

  // 自定义渲染
  customEmptyState?: ReactNode;
}
export default function ActivityProductGrid({
  id,
  title,
  className = '',
  staticProducts,
  staticTotal,
  apiConfig,
  showSort = false,
  showInfiniteScroll = false,
  showPagination = false,
  deviceType,
  wapColumns = 2,
  pcColumns = { lg: 3, xl: 4, '2xl': 4 },
  padding = { wap: 'px-[8px]', pc: 'px-[120px]' },
  stickyTop = { wap: 'top-[11.733vw]', pc: 'top-[100px]' },
  productCardProps = {},
  onProductClick,
  onProductExp,
  onSortChange,
  onPageChange,
  onLoadComplete,
  customEmptyState,
}: ActivityProductGridProps) {
  // 状态管理
  const [products, setProducts] = useState<ActivityProduct[]>(staticProducts || []);
  const [loading, setLoading] = useState(false);
  const [sortValue, setSortValue] = useState<string | number>(DEFAULT_SORT_VALUE);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(staticTotal || 0);
  const [hasMore, setHasMore] = useState(true);
  const [noData, setNoData] = useState(false);

  // 防止重复API调用的标记
  const hasInitializedRef = useRef(false);

  const currency = useStore((state) => state.currency);
  const setIsShowFooterNav = useStore((state) => state.setIsShowFooterNav);
  const inAppH5 = [EPlatform.ANDROID, EPlatform.IOS].includes(deviceType);
  const publicResourceData = useStore((state) => state.publicResourceData);

  const product_sort_new = useMemo(() => {
    try {
      return publicResourceData.config_names.product_sort_new;
    } catch {
      return [];
    }
  }, [publicResourceData]);
  const sortOptions = useMemo(() => {
    return product_sort_new.map((item) => {
      return {
        label: item.name,
        value: item.value || DEFAULT_SORT_VALUE,
      };
    });
  }, [product_sort_new]);

  // 判断设备类型
  const isWAP = deviceType !== EPlatform.WEB;

  // 确定使用哪种分页模式
  const usePagination = showPagination && !showInfiniteScroll;
  const useInfiniteScroll = showInfiniteScroll && !showPagination;

  // 获取间距类名
  const getPaddingClassName = () => {
    return isWAP ? padding.wap || 'px-[8px]' : padding.pc || 'px-[120px]';
  };

  // 获取吸顶距离类名
  const getStickyTopClassName = useCallback(() => {
    if (inAppH5) {
      return 'top-0';
    }
    return isWAP ? stickyTop.wap || 'top-[11.733vw]' : stickyTop.pc || 'top-[100px]';
  }, [inAppH5, isWAP, stickyTop]);

  // 替换图标函数
  const replaceIcons = (content: string) => {
    try {
      if (typeof content !== 'string') return content;

      return content
        .replace(/<i class="iconfont icon-shangbiao"><\/i>/g, '®')
        .replace(/<i class="iconfont icon-line-trademarkshangbiao"><\/i>/g, '™');
    } catch {
      return content;
    }
  };

  // API模式下获取商品列表
  const fetchProducts = useCallback(
    async (sort: string | number, currentPage: number, reset = false) => {
      if (!apiConfig?.group_id || !currency?.name) return;

      setLoading(true);
      // 只在WAP端控制底部导航
      if (isWAP) {
        setIsShowFooterNav(false);
      }
      try {
        const params: IProductListApiParams = {
          group_id:
            typeof apiConfig.group_id === 'string'
              ? parseInt(apiConfig.group_id, 10)
              : apiConfig.group_id,
          page: currentPage,
          limit: apiConfig.limit || 12,
          in_stock: apiConfig.in_stock ?? 1,
          is_sale: apiConfig.is_sale ?? 1,
          append_correlation: productCardProps.showCorrelation ? 1 : 2,
          currency: currency.name,
        };

        // 只有启用排序且排序值不是默认值时才添加order字段
        if (showSort && sort !== DEFAULT_SORT_VALUE) {
          params.order = sort.toString();
        }

        const response = await fetchGoodsList(params);

        if (response.code === 200 && response.data) {
          const newProducts = response.data.list || [];
          const totalCount = response.data.total || 0;

          setProducts((prev) => {
            // 分页模式下直接替换数据，无限滚动模式下追加数据
            const updatedProducts =
              reset || usePagination
                ? (newProducts as ActivityProduct[])
                : [...prev, ...(newProducts as ActivityProduct[])];
            const currentProductCount = updatedProducts.length;
            const hasMoreData =
              currentProductCount < totalCount && newProducts.length === (apiConfig.limit || 12);
            setHasMore(hasMoreData);

            // 通知父组件加载状态
            onLoadComplete?.(hasMoreData);

            // 判断是否显示底部导航（只在WAP端生效）
            if (isWAP) {
              if (currentProductCount >= totalCount) {
                setIsShowFooterNav(true);
              } else {
                // 做pg专题页时候发现报错了，不是每次都复现，在render这个组件的时候别的组件更新了
                setIsShowFooterNav(false);
              }
            }

            return updatedProducts;
          });

          setTotal(totalCount);
          setNoData(newProducts.length === 0 && currentPage === 1);
        } else {
          throw new Error(response.msg || 'Failed to fetch products');
        }
      } catch {
        if (currentPage === 1) {
          setNoData(true);
        }
      } finally {
        setLoading(false);
      }
    },
    [apiConfig, currency.name, usePagination, isWAP],
  );

  // 处理排序变化
  const handleSortChange = useCallback(
    (value: string | number) => {
      setSortValue(value);
      setPage(1);
      setHasMore(true);
      setNoData(false);

      if (apiConfig && currency?.name) {
        fetchProducts(value, 1, true);
      }

      onSortChange?.(value);

      // 滚动到商品列表开始位置，向上偏移20vw
      if (showSort) {
        try {
          const scrollTargetId = id ? `${id}-scroll-target` : 'scroll-target';
          const scrollTargetElement = document.getElementById(scrollTargetId);
          if (scrollTargetElement) {
            const elementRect = scrollTargetElement.getBoundingClientRect();
            const offsetTop = elementRect.top + window.pageYOffset;
            const offset20vw = window.innerWidth * 0.2; // 20vw转换为像素
            const targetScrollPosition = offsetTop - offset20vw;

            window.scrollTo({
              top: Math.max(0, targetScrollPosition), // 确保不会滚动到负数位置
              behavior: 'smooth',
            });
          }
        } catch {
          // 不进行任何操作
        }
      }
    },
    [fetchProducts, apiConfig, onSortChange, currency?.name],
  );

  // 处理加载更多（无限滚动模式）
  const handleLoadMore = useCallback(() => {
    if (!loading && hasMore && apiConfig && useInfiniteScroll && currency?.name) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchProducts(sortValue, nextPage, false);
    }
  }, [
    loading,
    hasMore,
    page,
    sortValue,
    fetchProducts,
    apiConfig,
    useInfiniteScroll,
    currency?.name,
  ]);

  // 处理分页器页面变化
  const handlePageChange = useCallback(
    (event: React.ChangeEvent<unknown>, value: number) => {
      if (usePagination && apiConfig && currency?.name) {
        setPage(value);
        fetchProducts(sortValue, value, true);
        onPageChange?.(value);

        // 滚动到顶部
        if (id) {
          const element = document.getElementById(id);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    },
    [usePagination, apiConfig, sortValue, fetchProducts, onPageChange, id, currency?.name],
  );

  // 初始化数据
  useEffect(() => {
    // 如果有静态数据，优先使用静态数据
    if (staticProducts && staticProducts.length) {
      // 只在WAP端控制底部导航
      if (isWAP) {
        setIsShowFooterNav(false);
      }
      setProducts(staticProducts);
      setNoData(staticProducts.length === 0);
      // 静态数据模式下，数据已经全部加载完成，显示底部导航（只在WAP端）
      if (isWAP) {
        setIsShowFooterNav(true);
      }
      hasInitializedRef.current = true; // 标记已初始化
      return; // 有静态数据时直接返回，不执行API请求
    }

    // 只有在没有静态数据且有apiConfig且未初始化时才请求API
    if (apiConfig?.group_id && currency?.name && !hasInitializedRef.current) {
      hasInitializedRef.current = true; // 标记已初始化，防止重复调用
      fetchProducts('default', 1, true);
    }
  }, [
    staticProducts, // 将staticProducts作为依赖项放在第一位
    apiConfig?.group_id,
    currency?.name, // 直接依赖currency，移除fetchProducts依赖
    isWAP,
    setIsShowFooterNav,
  ]);

  // 处理商品点击
  const handleProductClick = (product: ActivityProduct, index: number) => {
    onProductClick?.(product, index);
  };

  // 处理商品曝光
  const handleProductInView = (product: ActivityProduct, index: number) => {
    onProductExp?.(product, index);
  };

  // 生成网格类名
  const getGridClassName = () => {
    if (isWAP) {
      return `grid grid-cols-${wapColumns} gap-x-[8px] gap-y-[40px] lg:hidden`;
    } else {
      const lgCols = `lg:grid-cols-${pcColumns.lg}`;
      const xlCols = pcColumns.xl ? `xl:grid-cols-${pcColumns.xl}` : '';
      const xxlCols = pcColumns['2xl'] ? `2xl:grid-cols-${pcColumns['2xl']}` : '';
      return `hidden lg:grid ${lgCols} ${xlCols} ${xxlCols} gap-x-[16px] gap-y-[24px]`.trim();
    }
  };

  return (
    <div id={id} className={`scroll-mt-[100px] w-full ${className}`}>
      {/* 标题 */}
      {title &&
        (typeof title === 'string' ? (
          <div className={`pt-[24px] lg:pt-[54px] pb-[16px] lg:pb-[30px] mx-[30px]`}>
            <h2
              className="text-[20px] lg:text-[32px] font-bb text-center leading-[24px] lg:leading-[48px]"
              dangerouslySetInnerHTML={{ __html: replaceIcons(title) }}
            />
          </div>
        ) : (
          title
        ))}

      {/* 排序菜单 */}
      {/* PC端排序 */}
      {showSort && !isWAP && (
        <div
          className={`hidden lg:flex lg:justify-end lg:items-center sticky ${getStickyTopClassName()} z-[13] bg-[#ffffff] ${getPaddingClassName()} py-[10px]`}
        >
          <SortMenu
            options={sortOptions}
            value={sortValue}
            onChange={handleSortChange}
            showColon={false}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          />
        </div>
      )}
      {/* WAP端排序 - 使用sticky定位 */}
      {showSort && isWAP && (
        <div
          className={`lg:hidden z-[13] sticky ${getStickyTopClassName()} h-[36px] flex items-center justify-end bg-[#ffffff]`}
        >
          <div className="pr-[10px]">
            <SortMenu
              options={sortOptions}
              value={sortValue}
              onChange={handleSortChange}
              showColon={false}
            />
          </div>
        </div>
      )}

      {/* 滚动定位点 - 用于排序后的滚动定位 */}
      {showSort && <div id={id ? `${id}-scroll-target` : 'scroll-target'} className="h-0" />}

      {/* 商品列表 */}
      {products.length > 0 && (
        <div className={`${getPaddingClassName()} lg:pt-[7px] pb-[60px] lg:pb-[45px]`}>
          <div className={getGridClassName()}>
            {products.map((product, index) => {
              // 为ProductCard转换产品数据格式
              const productForCard = {
                ...product,
                name: product.short_name || product.name || product.shop_sku || '',
              };

              return (
                <div
                  key={`${product.shop_sku || product.id}-${index}`}
                  className={isWAP ? 'bg-none' : 'bg-white rounded-lg overflow-hidden'}
                >
                  <ProductCard
                    product={productForCard as any}
                    index={index}
                    showTryOn={productCardProps.showTryOn ?? false}
                    className={productCardProps.className || (isWAP ? 'bg-none' : 'w-full h-full')}
                    imageClassName={
                      productCardProps.imageClassName ||
                      (isWAP ? 'h-[176px] rounded-[12px]' : undefined)
                    }
                    showCorrelation={productCardProps.showCorrelation}
                    showReviewStar={productCardProps.showReviewStar}
                    handelClick={(cardIndex, cardProduct) =>
                      handleProductClick(cardProduct, cardIndex)
                    }
                    onInView={(cardIndex, cardProduct) =>
                      handleProductInView(cardProduct, cardIndex)
                    }
                    frameWidth="70%"
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 空数据状态 */}
      {noData &&
        !loading &&
        (customEmptyState || (
          <div className="flex flex-col items-center justify-center pt-[50px] pb-[100px] text-center">
            <Image
              className="no-data mx-auto mb-[10px] w-[118px] pb-[20px]"
              src="/goods-list/no-data.png"
              alt="No products"
              width={118}
              height={118}
              fetchPriority="high"
            />
            <p className="text-14 text-[#808080]">There are no products matching the category.</p>
          </div>
        ))}

      {/* 无限滚动组件 */}
      {useInfiniteScroll && products.length > 0 && (
        <InfiniteScroll
          loading={loading}
          onReachBottom={handleLoadMore}
          page={page}
          total={total}
          limit={apiConfig?.limit || 12}
          className="pb-[30px] lg:pb-[45px]"
        />
      )}

      {/* 分页器组件 */}
      {usePagination && products.length > 0 && total > (apiConfig?.limit || 20) && (
        <div className="pb-[30px] lg:pb-[45px]">
          <Pagination
            total={total}
            current={page}
            pageSize={apiConfig?.limit || 12}
            onChange={handlePageChange}
            className="mt-[20px] lg:mt-[40px]"
          />
        </div>
      )}
    </div>
  );
}
